import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import * as Mui from '@material-ui/core';
import toast from '~/services/toast';
import { CustomNumberInput } from '~/components/CustomNumberInput';

/**
 * Componente reutilizável para input de quantidade
 * @param {Object} props
 * @param {Object} props.produto - Produto com propriedades fracionavel, embalagemMultiplo, embalagemQuantidade
 * @param {number} props.value - Valor atual da quantidade
 * @param {function} props.onChange - Callback chamado quando a quantidade muda (retorna valor numérico)
 * @param {function} props.onBlur - Callback chamado no blur (opcional)
 * @param {function} props.onKeyPress - Callback chamado no keypress (opcional)
 * @param {boolean} props.disabled - Se o input está desabilitado
 * @param {boolean} props.autoFocus - Se deve focar automaticamente
 * @param {boolean} props.required - Se o campo é obrigatório
 * @param {string} props.label - Label do campo (opcional)
 * @param {boolean} props.showLabel - Se deve exibir a label (padrão: false)
 */
const QuantidadeInput = forwardRef(({
  produto,
  value,
  onChange,
  onBlur,
  onKeyPress,
  disabled = false,
  autoFocus = false,
  required = false,
  label = 'Quantidade (*)',
  showLabel = false,
  ...otherProps
}, ref) => {
  const quantidadeRef = useRef();

  useImperativeHandle(ref, () => ({
    focus: () => quantidadeRef?.current?.focus(),
    select: () => quantidadeRef?.current?.select(),
    value: quantidadeRef?.current?.value,
    setValue: (newValue) => {
      if (quantidadeRef.current) {
        quantidadeRef.current.value = newValue;
      }
    }
  }));

  const showToastIfDifferent = (originalQuantidade, novaQuantidade) => {
    if (originalQuantidade !== novaQuantidade) {
      toast.produtoMulti(
        `<b>${produto?.codigo} - ${produto?.descricao}</b> é múltiplo de embalagem ${produto?.embalagemQuantidade},
        e a quantidade vendida foi alterada de ${originalQuantidade} para ${novaQuantidade}`
      );
    }
  };

  const handleEmbalagemMultiplo = (quantidade) => {
    const quantidadeEmbalagem = produto?.embalagemQuantidade;
    const quantidadeRefValue = parseFloat(quantidade);

    if (quantidadeEmbalagem > 0 && quantidadeRefValue !== 0) {
      const resto = quantidadeRefValue % quantidadeEmbalagem;
      let quantidadeAjustada;

      if (resto >= quantidadeEmbalagem / 2) {
        quantidadeAjustada = quantidadeRefValue + (quantidadeEmbalagem - resto);
      } else {
        quantidadeAjustada = quantidadeRefValue - resto;
      }

      quantidadeAjustada = parseFloat(quantidadeAjustada.toFixed(2));

      if (quantidadeRef.current) {
        quantidadeRef.current.value = quantidadeAjustada;
      }

      showToastIfDifferent(quantidadeRefValue, quantidadeAjustada);

      if (onChange) {
        onChange(quantidadeAjustada);
      }
    }
  };

  const handleFracionavel = (quantidade) => {
    const valorFormatado = quantidade.replace(/\./g, '').replace(',', '.');
    const valorNumerico = parseFloat(valorFormatado);

    if (onChange && !isNaN(valorNumerico)) {
      onChange(valorNumerico);
    }
  };

  const handleDefault = (quantidade) => {
    const regex = /^[0-9\b]+$/;
    if (quantidade === '' || regex.test(quantidade)) {
      const valorFormatado = quantidade.replace(',', '.');
      const valorNumerico = parseFloat(valorFormatado);

      if (onChange && !isNaN(valorNumerico)) {
        onChange(valorNumerico);
      }
    }
  };

  const handleChangeInternal = (e) => {
    const quantidade = e.target.value;

    if (produto?.embalagemMultiplo) {
      // Para embalagem múltipla, só atualiza o state durante a digitação
      const valorNumerico = parseFloat(quantidade.replace(',', '.'));
      if (onChange && !isNaN(valorNumerico)) {
        onChange(valorNumerico);
      }
    } else if (produto?.fracionavel) {
      handleFracionavel(quantidade);
    } else {
      handleDefault(quantidade);
    }
  };

  const handleBlurInternal = (e) => {
    const quantidade = e.target.value;

    if (produto?.embalagemMultiplo) {
      handleEmbalagemMultiplo(quantidade);
    } else if (produto?.fracionavel) {
      handleFracionavel(quantidade);
    } else {
      handleDefault(quantidade);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyPressInternal = (ev) => {
    // Bloqueia entrada de ponto e vírgula em produtos não fracionáveis
    if (!produto?.fracionavel && (ev.key === '.' || ev.key === ',')) {
      ev.preventDefault();
    }

    if (onKeyPress) {
      onKeyPress(ev);
    }
  };

  return (
    <>
      {showLabel && (
        <Mui.Typography variant="caption">{label}</Mui.Typography>
      )}

      {produto?.fracionavel && !produto?.embalagemMultiplo ? (
        <Mui.TextField
          id="itemQuantidade"
          inputRef={quantidadeRef}
          type="numeric"
          variant="outlined"
          fullWidth
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={handleChangeInternal}
          onBlur={handleBlurInternal}
          value={value ?? 1}
          onKeyPress={handleKeyPressInternal}
          InputProps={{
            style: {
              height: 44
            },
            inputComponent: CustomNumberInput
          }}
          {...otherProps}
        />
      ) : (
        <Mui.TextField
          id="itemQuantidade"
          inputRef={quantidadeRef}
          type="number"
          variant="outlined"
          fullWidth
          autoFocus={autoFocus}
          required={required}
          InputProps={{
            style: {
              height: 44
            }
          }}
          disabled={disabled}
          onChange={handleChangeInternal}
          onBlur={handleBlurInternal}
          value={value ?? 1}
          pattern="[0-9]*"
          onKeyPress={handleKeyPressInternal}
          {...otherProps}
        />
      )}
    </>
  );
});

QuantidadeInput.displayName = 'QuantidadeInput';

export default QuantidadeInput;
